/** LNAT workspace scoring: Section A raw → /42, Section B is an essay (gates completion, no score). */

import {
  clearPaperScores,
  loadPaperScores,
  readUserCache,
  savePaperScore,
  writeUserCache,
} from './paperProgressCache';

export const LNAT_SCORES_KEY = 'lnat_paper_scores';
export const LNAT_TARGET_KEY = 'lnat_workspace_target';

/** Normalise a possibly-shorter mock to the real exam's /42 Section A scale. */
export function sectionAScore(correct, total) {
  const t = Number(total);
  if (!t || t <= 0) return null;
  const c = Math.max(0, Number(correct) || 0);
  return Math.round((c / t) * 42 * 10) / 10;
}

export function formatLnatScore(score) {
  if (score == null || !Number.isFinite(Number(score))) return '—';
  return `${Number(score).toFixed(1)}/42`;
}

export function formatPaperRaw(correct, total) {
  if (total == null || total <= 0 || correct == null) return '—';
  return `${correct}/${total}`;
}

export function loadLnatPaperScores(userId) {
  return loadPaperScores(LNAT_SCORES_KEY, userId);
}

export function saveLnatPaperScore(userId, paperId, score = {}) {
  savePaperScore(LNAT_SCORES_KEY, userId, paperId, score);
}

export function clearLnatPaperScores(userId, paperIds = []) {
  clearPaperScores(LNAT_SCORES_KEY, userId, paperIds);
}

/** Forget the user's cached scores for a sitting's papers (a retake). */
export function resetLnatSittingProgress(userId, paperIds = []) {
  clearLnatPaperScores(userId, paperIds);
}

/**
 * Section A drives the score; Section B is an essay that only gates completion.
 */
export function sittingScoreFromLnatPapers(sectionAId, sectionBId, scoresMap = {}) {
  const a = sectionAId ? scoresMap[String(sectionAId)] : null;
  const b = sectionBId ? scoresMap[String(sectionBId)] : null;

  const aDone = Boolean(a && a.total > 0);
  const bDone = Boolean(b && (b.dbStatus === 'completed' || b.total > 0));

  const score = aDone ? sectionAScore(a.correct, a.total) : null;
  const mode = a?.mode || b?.mode || null;
  const status = aDone && bDone ? 'done' : aDone || bDone ? 'progress' : 'none';

  return {
    a: aDone ? a : null,
    b: bDone ? b : null,
    score,
    mode,
    status,
  };
}

/** 2-paper status from DB records, local to LNAT (TMUA's helper lives in a shared file). */
export function lnatStatusFromDbPapers(paperIds = [], dbPapers = {}) {
  const statuses = paperIds.map((id) => (id ? dbPapers[String(id)]?.status : null)).filter(Boolean);
  if (!statuses.length) return 'none';
  if (statuses.every((s) => s === 'completed')) return 'done';
  if (statuses.some((s) => s === 'in_progress' || s === 'completed')) return 'progress';
  return 'none';
}

export function loadLnatTarget(userId) {
  const fallback = { value: 27, label: 'London School of Economics (LSE) — LLB Law', sectionB: 'Medium/High Weight', custom: false };
  return readUserCache(LNAT_TARGET_KEY, userId, fallback);
}

export function saveLnatTarget(userId, target) {
  writeUserCache(LNAT_TARGET_KEY, userId, target);
}
